import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { AssistantTask } from '@accounting/shared';
import { PROVIDER_LABELS, useConnections } from '../../hooks/useAssistant';
import { useCreateTask, useDeleteTask, useTaskLists, useTasks, useUpdateTask, useUpdateTaskList } from '../../hooks/useAssistantTasks';
import { TaskEditor } from '../../components/assistant/TaskEditor';
import { TaskRow } from '../../components/assistant/TaskRow';
import { ApiError } from '../../lib/api';
import { localDateKey } from '../../lib/calendarGrid';

export const Route = createFileRoute('/_auth/assistant/tasks')({ component: TasksPage });

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

function TasksPage() {
  const today = localDateKey(new Date());
  const connections = useConnections();
  const lists = useTaskLists();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [showDone, setShowDone] = useState(false);
  const [editing, setEditing] = useState<AssistantTask | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const visibleIds = useMemo(() => (connections.data ?? []).filter((c) => !hidden.has(c.id)).map((c) => c.id), [connections.data, hidden]);
  const open = useTasks('open', visibleIds);
  const done = useTasks('completed', visibleIds);

  const create = useCreateTask();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const updateList = useUpdateTaskList();
  const busy = create.isPending || update.isPending || remove.isPending;

  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [listId, setListId] = useState('');

  const groups = useMemo(() => {
    const all = open.data ?? [];
    return {
      overdue: all.filter((t) => t.dueDate && t.dueDate < today),
      today: all.filter((t) => t.dueDate === today),
      upcoming: all.filter((t) => t.dueDate && t.dueDate > today),
      undated: all.filter((t) => !t.dueDate),
    };
  }, [open.data, today]);

  const toggle = (t: AssistantTask) =>
    update.mutate({ id: t.id, status: t.status === 'completed' ? 'open' : 'completed' }, { onError: (e) => setMessage(errorMessage(e)) });
  const del = (t: AssistantTask) => {
    if (window.confirm(`Delete "${t.title}"?${t.provider !== 'native' ? ` It is removed from ${PROVIDER_LABELS[t.provider] ?? t.provider} too.` : ''}`)) {
      remove.mutate(t.id, { onError: (e) => setMessage(errorMessage(e)) });
    }
  };

  const writableLists = (lists.data ?? []).filter((l) => l.canWrite && l.isSelected);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1000 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Tasks</h1>
        <span className="muted">
          {groups.overdue.length > 0 ? `${groups.overdue.length} overdue · ` : ''}
          {groups.today.length} due today
        </span>
      </header>

      {message && (
        <div className="card pa-banner pa-banner--err" role="alert">
          <span>{message}</span>
          <button type="button" className="link-button" onClick={() => setMessage(null)}>Dismiss</button>
        </div>
      )}

      <form
        className="card pa-quickadd"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          create.mutate(
            { title: title.trim(), dueDate: due || null, listId: listId || undefined },
            {
              onSuccess: () => {
                setTitle('');
                setDue('');
              },
              onError: (err) => setMessage(errorMessage(err)),
            },
          );
        }}
      >
        <input type="text" placeholder="Add a task…" value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} title="Due date" />
        <select value={listId} onChange={(e) => setListId(e.target.value)} title="Where the task lives">
          <option value="">Assistant only</option>
          {writableLists.map((l) => (
            <option key={l.id} value={l.id}>
              {l.connectionLabel} · {l.name}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary" disabled={busy || !title.trim()}>
          Add
        </button>
      </form>

      {connections.data && connections.data.length > 0 && (
        <div className="pa-chips">
          {connections.data.map((c) => {
            const off = hidden.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                className={`pa-chip${off ? ' pa-chip--off' : ''}`}
                aria-pressed={!off}
                onClick={() =>
                  setHidden((prev) => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    return next;
                  })
                }
              >
                <span className="pa-dot" style={{ background: c.color }} aria-hidden="true" />
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      {open.isLoading && <p className="muted">Loading…</p>}
      {open.data && open.data.length === 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Nothing open. Add one above{connections.data?.length ? '' : <>, or <Link to="/assistant/connections">connect an account</Link> to pull in To Do or Google Tasks</>}.
          </p>
        </div>
      )}

      {(
        [
          ['Overdue', groups.overdue],
          ['Today', groups.today],
          ['Upcoming', groups.upcoming],
          ['No date', groups.undated],
        ] as const
      ).map(([label, items]) =>
        items.length === 0 ? null : (
          <section key={label} className="card pa-task-group">
            <h3 className="pa-task-group__heading">
              {label} <span className="muted">{items.length}</span>
            </h3>
            {items.map((t) => (
              <TaskRow key={t.id} task={t} today={today} busy={busy} onToggle={toggle} onEdit={setEditing} onDelete={del} />
            ))}
          </section>
        ),
      )}

      <section className="card pa-task-group">
        <h3 className="pa-task-group__heading">
          <button type="button" className="link-button" onClick={() => setShowDone((s) => !s)}>
            {showDone ? '▾' : '▸'} Completed {done.data ? <span className="muted">{done.data.length}</span> : null}
          </button>
        </h3>
        {showDone &&
          (done.data ?? []).map((t) => (
            <TaskRow key={t.id} task={t} today={today} busy={busy} onToggle={toggle} onEdit={setEditing} onDelete={del} />
          ))}
      </section>

      {lists.data && lists.data.length > 0 && (
        <section>
          <h2 style={{ marginBottom: 4 }}>Lists</h2>
          <div className="card">
            {lists.data.map((l) => (
              <label key={l.id} className="pa-calendar-row">
                <input type="checkbox" checked={l.isSelected} disabled={updateList.isPending} onChange={(e) => updateList.mutate({ id: l.id, isSelected: e.target.checked })} />
                <span className="pa-dot" style={{ background: l.color }} aria-hidden="true" />
                <span style={{ flex: 1 }}>
                  {l.connectionLabel} · {l.name}
                </span>
                {l.isPrimary && <span className="tag">primary</span>}
                {!l.canWrite && <span className="tag">read-only</span>}
              </label>
            ))}
          </div>
        </section>
      )}

      {editing && (
        <TaskEditor
          task={editing}
          busy={update.isPending}
          error={update.isError ? errorMessage(update.error) : null}
          onSave={(patch) => update.mutate({ id: editing.id, ...patch }, { onSuccess: () => setEditing(null) })}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
